import React, { useEffect, useState } from 'react';
import { Table, Button, Form, Card, Modal, Row, Col } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import Message from '../components/Message';
import Loader from '../components/Loader';
import Meta from '../components/Meta';
import {
  listServiceClients,
  createServiceClient,
  rotateServiceClient,
  revokeServiceClient,
  resetServiceClientErrors,
} from '../slices/serviceClientSlice';

// Must match backend SERVICE_SCOPES/OPT_IN_SCOPES/WRITE_SCOPES (backend/models/aliExpressServiceClientModel.js).
// There's no endpoint to fetch this list, so it's mirrored here.
const READ_SCOPES = ['status:read', 'products:read', 'categories:read'];
// Opt-in: returns the complete unfiltered AliExpress response, never selected by default.
const RAW_SCOPES = ['products:raw'];
const WRITE_SCOPES = ['orders:write', 'orders:read'];

// Mirrors the backend's own stricter ceiling for a credential that can spend
// money (aliExpressServiceClientController.js MAX_EXPIRY_DAYS/MAX_RPM) — kept
// in sync here so the form can't even offer a value the API will reject.
const MAX_EXPIRY_DAYS = { write: 90, read: 366 };
const MAX_RPM = { write: 30, read: 600 };
const isWriteScoped = (scopes) => scopes.some((s) => WRITE_SCOPES.includes(s));

const isoDaysFromNow = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

const statusFor = (client) => {
  if (client.revokedAt) return { label: 'Revoked', variant: 'secondary' };
  if ((client.daysUntilExpiry ?? 1) <= 0) return { label: 'Expired', variant: 'danger' };
  if (client.expiringSoon) return { label: `Expires in ${client.daysUntilExpiry}d`, variant: 'warning' };
  return { label: 'Active', variant: 'success' };
};

const OneTimeKeyReveal = ({ keyValue, onDismiss }) => {
  const [copyState, setCopyState] = useState('idle'); // idle | copied | failed
  const inputRef = React.useRef(null);

  const selectAll = (e) => e.target.select();

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(keyValue);
      setCopyState('copied');
    } catch {
      // Clipboard-write can be silently blocked by browser/permissions-policy
      // settings - fall back to auto-selecting the field so a manual
      // Ctrl+C still gets the exact, complete value instead of a partial
      // manual drag-select across a long hex string.
      setCopyState('failed');
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  };

  return (
    <Message variant='warning'>
      <strong>Copy this key now — it will not be shown again.</strong>
      <div className='mt-2 d-flex gap-2 align-items-center flex-wrap'>
        <Form.Control
          ref={inputRef}
          readOnly
          onFocus={selectAll}
          onClick={selectAll}
          value={keyValue}
          style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
        />
        <Button size='sm' variant='outline-dark' onClick={copy}>{copyState === 'copied' ? 'Copied' : 'Copy'}</Button>
      </div>
      {copyState === 'failed' && (
        <div className='mt-2'>
          <small className='text-danger'>
            Automatic copy was blocked by the browser. The field above is now selected — press Ctrl+C (Cmd+C on Mac) to copy it manually, rather than dragging to select, so you get the exact value.
          </small>
        </div>
      )}
      <div className='mt-2'>
        <small>
          Set it in the consuming app's own environment (e.g. <code>heroku config:set LOTUSTOR_SERVICE_API_KEY=...</code>) —
          run that yourself, in your own terminal, so the key never has to pass through anyone else.
        </small>
      </div>
      <div className='mt-2'>
        <Button size='sm' variant='outline-secondary' onClick={onDismiss}>Done</Button>
      </div>
    </Message>
  );
};

const AdminServiceClientsScreen = ({ history }) => {
  const dispatch = useDispatch();
  const userState = useSelector((state) => state.user);
  const { userInfo } = userState;
  const serviceClientState = useSelector((state) => state.serviceClient);
  const { clients, loading, error, creating, createError, rotating, rotateError, revoking, revokeError } = serviceClientState;

  useEffect(() => {
    if (userInfo && userInfo.isAdmin) {
      dispatch(listServiceClients());
    } else {
      history.push('/login');
    }
  }, [dispatch, history, userInfo]);

  const [form, setForm] = useState({
    name: '',
    sellerId: '',
    // Never defaults to a write scope - a credential must opt into spending
    // money, the same rule the backend enforces (DEFAULT_SCOPES).
    scopes: [...READ_SCOPES],
    requestsPerMinute: 60,
    expiresAt: isoDaysFromNow(90),
  });
  const [revealedKey, setRevealedKey] = useState(null);
  const writeScoped = isWriteScoped(form.scopes);

  const toggleScope = (scope) => {
    setForm((f) => {
      const scopes = f.scopes.includes(scope) ? f.scopes.filter((s) => s !== scope) : [...f.scopes, scope];
      const cap = isWriteScoped(scopes) ? 'write' : 'read';
      // Clamp down, never up - switching on a write scope must not silently
      // relax an existing tighter setting, and switching it off can leave
      // the more generous read ceiling for the admin to raise manually.
      const maxDate = isoDaysFromNow(MAX_EXPIRY_DAYS[cap]);
      return {
        ...f,
        scopes,
        requestsPerMinute: Math.min(f.requestsPerMinute, MAX_RPM[cap]),
        expiresAt: f.expiresAt > maxDate ? maxDate : f.expiresAt,
      };
    });
  };

  const submitCreate = async (e) => {
    e.preventDefault();
    dispatch(resetServiceClientErrors());
    try {
      const result = await dispatch(createServiceClient({
        name: form.name.trim(),
        sellerId: form.sellerId.trim(),
        scopes: form.scopes,
        requestsPerMinute: Number(form.requestsPerMinute),
        expiresAt: new Date(form.expiresAt).toISOString(),
      })).unwrap();
      setRevealedKey(result.key);
      setForm((f) => ({ ...f, name: '', sellerId: '' }));
    } catch {
      // createError is already set by the rejected reducer case
    }
  };

  const [rotateTarget, setRotateTarget] = useState(null);
  const [rotateExpiresAt, setRotateExpiresAt] = useState(isoDaysFromNow(90));

  const openRotate = (client) => {
    dispatch(resetServiceClientErrors());
    setRotateTarget(client);
    // Rotate keeps the client's existing scopes (can't be changed here), so
    // an orders:write client's own 90-day cap applies to its default too.
    setRotateExpiresAt(isoDaysFromNow(isWriteScoped(client.scopes) ? MAX_EXPIRY_DAYS.write : MAX_EXPIRY_DAYS.read));
  };

  const confirmRotate = async () => {
    try {
      const result = await dispatch(rotateServiceClient({
        id: rotateTarget.id,
        expiresAt: new Date(rotateExpiresAt).toISOString(),
      })).unwrap();
      setRotateTarget(null);
      setRevealedKey(result.key);
    } catch {
      // rotateError is already set by the rejected reducer case
    }
  };

  const revokeHandler = (client) => {
    if (window.confirm(`Revoke "${client.name}"? This takes effect immediately and cannot be undone.`)) {
      dispatch(revokeServiceClient(client.id));
    }
  };

  return (
    <>
      <Meta title='Admin — AliExpress Service Credentials — The Lotustor' robots='noindex,follow' />
      <h1>AliExpress Service Credentials</h1>
      <p className='text-muted'>
        Credentials issued to trusted consuming apps (e.g. myInventorySyncLister) for the AliExpress
        service API — read-only by default, or order-placement if <code>orders:write</code>/
        <code>orders:read</code> is selected below. Keys are shown once, at creation or rotation, and never again.
      </p>

      {revealedKey && <OneTimeKeyReveal keyValue={revealedKey} onDismiss={() => setRevealedKey(null)} />}

      <Card className='mb-4'>
        <Card.Body>
          <Card.Title>Issue a new credential</Card.Title>
          {createError && <Message variant='danger'>{createError}</Message>}
          <Form onSubmit={submitCreate}>
            <Row className='g-2'>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Name</Form.Label>
                  <Form.Control
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    placeholder='e.g. myInventorySyncLister production'
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Connected seller ID</Form.Label>
                  <Form.Control
                    value={form.sellerId}
                    onChange={(e) => setForm((f) => ({ ...f, sellerId: e.target.value }))}
                    placeholder='Existing connected AliExpress seller'
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group>
                  <Form.Label>Limit / min {writeScoped && <span className='text-muted'>(max {MAX_RPM.write})</span>}</Form.Label>
                  <Form.Control
                    type='number'
                    min={1}
                    max={writeScoped ? MAX_RPM.write : MAX_RPM.read}
                    value={form.requestsPerMinute}
                    onChange={(e) => setForm((f) => ({ ...f, requestsPerMinute: e.target.value }))}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group>
                  <Form.Label>Expires {writeScoped && <span className='text-muted'>(max {MAX_EXPIRY_DAYS.write}d)</span>}</Form.Label>
                  <Form.Control
                    type='date'
                    max={isoDaysFromNow(writeScoped ? MAX_EXPIRY_DAYS.write : MAX_EXPIRY_DAYS.read)}
                    value={form.expiresAt}
                    onChange={(e) => setForm((f) => ({ ...f, expiresAt: e.target.value }))}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            <div className='mt-3'>
              <div className='text-uppercase text-muted' style={{ fontSize: '0.75rem', letterSpacing: '0.05em' }}>Read scopes</div>
              <div className='mt-1 d-flex gap-3 flex-wrap'>
                {READ_SCOPES.map((scope) => (
                  <Form.Check
                    key={scope}
                    type='checkbox'
                    id={`scope-${scope}`}
                    label={scope}
                    checked={form.scopes.includes(scope)}
                    onChange={() => toggleScope(scope)}
                  />
                ))}
              </div>
              <div className='text-uppercase text-muted mt-3' style={{ fontSize: '0.75rem', letterSpacing: '0.05em' }}>
                Raw data scope
              </div>
              <div className='mt-1 d-flex gap-3 flex-wrap'>
                {RAW_SCOPES.map((scope) => (
                  <Form.Check
                    key={scope}
                    type='checkbox'
                    id={`scope-${scope}`}
                    label={scope}
                    checked={form.scopes.includes(scope)}
                    onChange={() => toggleScope(scope)}
                  />
                ))}
              </div>
              {form.scopes.includes('products:raw') && (
                <div className='mt-2'>
                  <Message variant='warning'>
                    <strong>products:raw</strong> lets this credential read the complete, unfiltered AliExpress
                    response for a product, including supplier and logistics details the normal product view
                    leaves out. It is never selected by default.
                  </Message>
                </div>
              )}
              <div className='text-uppercase text-muted mt-3' style={{ fontSize: '0.75rem', letterSpacing: '0.05em' }}>
                Order-placement scopes
              </div>
              <div className='mt-1 d-flex gap-3 flex-wrap'>
                {WRITE_SCOPES.map((scope) => (
                  <Form.Check
                    key={scope}
                    type='checkbox'
                    id={`scope-${scope}`}
                    label={scope}
                    checked={form.scopes.includes(scope)}
                    onChange={() => toggleScope(scope)}
                  />
                ))}
              </div>
              {writeScoped && (
                <div className='mt-2'>
                  <Message variant='warning'>
                    <strong>orders:write</strong> lets this credential place real, paid AliExpress orders.
                    Order-placement credentials are capped at {MAX_EXPIRY_DAYS.write} days and{' '}
                    {MAX_RPM.write} requests/minute — stricter than read-only credentials — and are never
                    selected by default.
                  </Message>
                </div>
              )}
            </div>
            <Button type='submit' className='mt-3' disabled={creating || !form.scopes.length}>
              {creating ? 'Issuing…' : 'Issue credential'}
            </Button>
          </Form>
        </Card.Body>
      </Card>

      {rotateError && <Message variant='danger'>{rotateError}</Message>}
      {revokeError && <Message variant='danger'>{revokeError}</Message>}

      {loading ? (
        <Loader />
      ) : error ? (
        <Message variant='danger'>{error}</Message>
      ) : (
        <Table striped bordered hover responsive size='sm'>
          <thead>
            <tr>
              <th>Name</th>
              <th>Seller ID</th>
              <th>Scopes</th>
              <th>Limit/min</th>
              <th>Expires</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {clients.length === 0 && (
              <tr><td colSpan={7} className='text-muted text-center'>No service credentials issued yet.</td></tr>
            )}
            {clients.map((client) => {
              const status = statusFor(client);
              const active = !client.revokedAt;
              return (
                <tr key={client.id}>
                  <td>{client.name}</td>
                  <td><code>{client.sellerId}</code></td>
                  <td><small>{client.scopes.join(', ')}</small></td>
                  <td>{client.requestsPerMinute}</td>
                  <td>{new Date(client.expiresAt).toLocaleDateString()}</td>
                  <td><span className={`badge bg-${status.variant}`}>{status.label}</span></td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <Button
                      size='sm'
                      variant='outline-primary'
                      className='me-1'
                      disabled={!active || rotating}
                      onClick={() => openRotate(client)}
                    >
                      Rotate
                    </Button>
                    <Button
                      size='sm'
                      variant='outline-danger'
                      disabled={!active || revoking}
                      onClick={() => revokeHandler(client)}
                    >
                      Revoke
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}

      <Modal show={!!rotateTarget} onHide={() => setRotateTarget(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Rotate "{rotateTarget?.name}"</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>The current key stops working the instant this completes. Set the new expiry:</p>
          {rotateTarget && isWriteScoped(rotateTarget.scopes) && (
            <Message variant='warning'>
              This is an order-placement credential ({rotateTarget.scopes.filter((s) => WRITE_SCOPES.includes(s)).join(', ')}) —
              expiry is capped at {MAX_EXPIRY_DAYS.write} days.
            </Message>
          )}
          <Form.Group>
            <Form.Label>New expiry</Form.Label>
            <Form.Control
              type='date'
              max={rotateTarget ? isoDaysFromNow(isWriteScoped(rotateTarget.scopes) ? MAX_EXPIRY_DAYS.write : MAX_EXPIRY_DAYS.read) : undefined}
              value={rotateExpiresAt}
              onChange={(e) => setRotateExpiresAt(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setRotateTarget(null)}>Cancel</Button>
          <Button variant='primary' onClick={confirmRotate} disabled={rotating}>
            {rotating ? 'Rotating…' : 'Rotate & show new key'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AdminServiceClientsScreen;
